/**
 * KardexInventario.jsx · v101.120.0 · Kardex por bodega: ingresos y egresos de Odoo con saldo por producto y la OT
 * de cada movimiento (trazabilidad: tarea de Odoo, evento de cierre de AlfaField o serie).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Warehouse, Search, Loader2, Download, ArrowDownLeft, ArrowUpRight, ArrowLeftRight, Link2, RefreshCw, X,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const fmtDate = (iso) => (iso ? new Date(iso + (iso.endsWith("Z") ? "" : "Z")).toLocaleString("es-EC", {
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Guayaquil",
}) : "—");
const num = (v) => (v ? Number(v).toLocaleString("es-EC", { maximumFractionDigits: 3 }) : "");

export default function KardexInventario() {
  const [locQ, setLocQ] = useState("");
  const [locs, setLocs] = useState([]);
  const [loc, setLoc] = useState(null);
  const [desde, setDesde] = useState(daysAgo(30));
  const [hasta, setHasta] = useState(today());
  const [tipo, setTipo] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  // Búsqueda en varias bodegas: producto, serie, bodega, sucursal
  const [mode, setMode] = useState("bodega"); // "bodega" | "buscar"
  const [sProd, setSProd] = useState("");
  const [sSerie, setSSerie] = useState("");
  const [sBodega, setSBodega] = useState("");
  const [sSucursal, setSSucursal] = useState("");

  useEffect(() => {
    const t = setTimeout(async () => {
      setLocLoading(true);
      try {
        const { data } = await api.get("/v1/kardex/locations", { params: { q: locQ } });
        setLocs(data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudieron cargar las bodegas");
      } finally { setLocLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [locQ]);

  const params = useMemo(() => ({
    ...(mode === "bodega" ? { location_id: loc?.id } : {
      ...(sProd.trim() ? { producto: sProd.trim() } : {}), ...(sSerie.trim() ? { serie: sSerie.trim() } : {}),
      ...(sBodega.trim() ? { bodega: sBodega.trim() } : {}), ...(sSucursal.trim() ? { sucursal: sSucursal.trim() } : {}),
    }),
    desde, hasta, ...(tipo ? { tipo } : {}), ...(q.trim() ? { q: q.trim() } : {}),
  }), [mode, loc, sProd, sSerie, sBodega, sSucursal, desde, hasta, tipo, q]);
  const canQuery = mode === "bodega" ? !!loc : !!(sProd.trim() || sSerie.trim() || sBodega.trim() || sSucursal.trim());

  const load = async () => {
    if (!canQuery) { toast.error(mode === "bodega" ? "Elija una bodega" : "Escriba producto, serie, bodega o sucursal"); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/v1/kardex", { params, timeout: 180000 });
      setData(data);
      if (data.truncated) toast.warning("Hay más de 5.000 movimientos: acote el rango de fechas.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el kardex");
    } finally { setLoading(false); }
  };

  const exportCsv = async () => {
    if (!canQuery) return;
    try {
      const resp = await api.get("/v1/kardex/export.csv", { params, responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kardex_${mode === "bodega" ? (loc?.name || loc?.id) : "busqueda"}_${desde}_${hasta}.csv`.replace(/\s+/g, "_");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  const t = data?.totals || {};
  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="kardex-page">
        <div className="flex items-center gap-2">
          <Warehouse className="text-[#0A3747]" size={22} />
          <h1 className="text-xl font-black text-[#0A3747]">Kardex de inventario</h1>
          <span className="text-xs text-gray-500">Ingresos y egresos por bodega (Odoo) con la OT de cada movimiento</span>
        </div>

        {/* Modo */}
        <div className="flex gap-2">
          {[["bodega", "Kardex de una bodega"], ["buscar", "Buscar por producto, serie, bodega o sucursal"]].map(([k, l]) => (
            <button key={k} onClick={() => { setMode(k); setData(null); }} data-testid={`kardex-mode-${k}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${mode === k ? "bg-[#0A3747] text-white border-[#0A3747]" : "bg-white text-gray-600 border-gray-300"}`}>{l}</button>
          ))}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-end">
          {mode === "buscar" && (
            <div className="lg:col-span-5 grid grid-cols-2 gap-2" data-testid="kardex-search-fields">
              {[["Producto", sProd, setSProd, "Nombre o código"], ["Serie", sSerie, setSSerie, "Serie / lote"],
                ["Bodega", sBodega, setSBodega, "Nombre de bodega"], ["Sucursal", sSucursal, setSSucursal, "Ej.: Quito, QT"]].map(([l, v, set, ph]) => (
                <div key={l}>
                  <label className="text-[10px] font-bold uppercase text-gray-500">{l}</label>
                  <input value={v} onChange={(e) => set(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={ph}
                    className="mt-1 w-full px-3 py-2 text-xs rounded-xl border border-gray-300" />
                </div>
              ))}
            </div>
          )}
          {mode === "bodega" && <div className="lg:col-span-5 relative">
            <label className="text-[10px] font-bold uppercase text-gray-500">Bodega</label>
            {loc ? (
              <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-xl border border-[#0A3747] bg-[#F0F7F9]">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-[#0A3747] truncate">{loc.complete_name}</div>
                  {!!loc.technicians?.length && <div className="text-[10px] text-gray-500 truncate">{loc.technicians.join(", ")}</div>}
                </div>
                <button onClick={() => { setLoc(null); setData(null); }} className="text-gray-400 hover:text-gray-700"><X size={14} /></button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={locQ} onChange={(e) => setLocQ(e.target.value)} placeholder="Buscar bodega o técnico…"
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-gray-300" data-testid="kardex-loc-search" />
                  {locLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
                </div>
                <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg">
                  {locs.filter((l) => !locQ || (l.complete_name + " " + (l.technicians || []).join(" ")).toLowerCase().includes(locQ.toLowerCase()))
                    .slice(0, 60).map((l) => (
                      <button key={l.id} onClick={() => setLoc(l)} className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100">
                        <div className="text-xs font-semibold text-gray-800">{l.complete_name}</div>
                        {!!l.technicians?.length && <div className="text-[10px] text-gray-500">{l.technicians.join(", ")}</div>}
                      </button>
                    ))}
                </div>
              </>
            )}
          </div>}
          <div className="lg:col-span-2">
            <label className="text-[10px] font-bold uppercase text-gray-500">Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="mt-1 w-full px-3 py-2 text-xs rounded-xl border border-gray-300" />
          </div>
          <div className="lg:col-span-2">
            <label className="text-[10px] font-bold uppercase text-gray-500">Hasta</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="mt-1 w-full px-3 py-2 text-xs rounded-xl border border-gray-300" />
          </div>
          <div className="lg:col-span-3 flex gap-2">
            <button onClick={load} disabled={!canQuery || loading} data-testid="kardex-load"
              className="flex-1 px-3 py-2 rounded-xl bg-[#0A3747] text-white text-xs font-bold disabled:opacity-40 flex items-center justify-center gap-1.5">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Consultar
            </button>
            <button onClick={exportCsv} disabled={!data} className="px-3 py-2 rounded-xl border border-gray-300 text-xs font-bold disabled:opacity-40 flex items-center gap-1.5">
              <Download size={14} /> CSV
            </button>
          </div>
          <div className="lg:col-span-12 flex flex-wrap gap-2 items-center">
            {[["", "Todos"], ["in", "Ingresos"], ["out", "Egresos"]].map(([k, l]) => (
              <button key={k} onClick={() => setTipo(k)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${tipo === k ? "bg-[#0A3747] text-white border-[#0A3747]" : "bg-white text-gray-600 border-gray-300"}`}>{l}</button>
            ))}
            <div className="relative flex-1 min-w-[220px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Filtrar por producto, serie, OT, cliente, documento o técnico (Enter)"
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-full border border-gray-300" />
            </div>
          </div>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Ingresos" value={num(t.ingresos) || "0"} color="#15803D" />
              <Kpi label="Egresos" value={num(t.egresos) || "0"} color="#B91C1C" />
              <Kpi label="Movimientos" value={t.movimientos ?? 0} color="#0A3747" />
              <Kpi label="Con OT identificada" value={`${t.con_ot ?? 0} / ${t.movimientos ?? 0}`} color="#7C3AED" />
            </div>
            <div className="text-[11px] text-gray-500">
              {data.location?.complete_name} · {data.sub_locations} sub-ubicaciones · {data.desde} → {data.hasta} ·
              {data.single
                ? <>saldo inicial en {data.opening_products} productos. El saldo es por producto (incluye lo anterior a «Desde»).</>
                : <>búsqueda en {data.sub_locations + 1} bodegas; «Transferencia» = movimiento entre dos de ellas.</>}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Documento</th>
                    <th className="px-3 py-2 text-left">Producto</th>
                    <th className="px-3 py-2 text-left">Serie</th>
                    <th className="px-3 py-2 text-right">Entrada</th>
                    <th className="px-3 py-2 text-right">Salida</th>
                    {data.single && <th className="px-3 py-2 text-right">Saldo</th>}
                    <th className="px-3 py-2 text-left">OT</th>
                    <th className="px-3 py-2 text-left">Cliente / técnico</th>
                    <th className="px-3 py-2 text-left">Desde → Hacia</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).map((r) => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50 align-top">
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.fecha)}</td>
                      <td className="px-3 py-2">
                        {r.tipo === "INGRESO"
                          ? <span className="inline-flex items-center gap-1 text-[#15803D] font-bold"><ArrowDownLeft size={12} />Ingreso</span>
                          : r.tipo === "EGRESO"
                            ? <span className="inline-flex items-center gap-1 text-[#B91C1C] font-bold"><ArrowUpRight size={12} />Egreso</span>
                            : <span className="inline-flex items-center gap-1 text-[#2563EB] font-bold"><ArrowLeftRight size={12} />Transferencia</span>}
                      </td>
                      <td className="px-3 py-2"><div className="font-mono">{r.documento}</div>{r.origen_doc && <div className="text-[10px] text-gray-400">{r.origen_doc}</div>}</td>
                      <td className="px-3 py-2"><div className="font-semibold text-gray-800">{r.producto}</div>{r.codigo && <div className="text-[10px] text-gray-400">{r.codigo}</div>}</td>
                      <td className="px-3 py-2 font-mono">{r.serie || ""}</td>
                      <td className="px-3 py-2 text-right text-[#15803D] font-semibold">{r.tipo === "TRANSFERENCIA" ? <span className="text-[#2563EB]">{num(r.cantidad)}</span> : num(r.entrada)}</td>
                      <td className="px-3 py-2 text-right text-[#B91C1C] font-semibold">{num(r.salida)}</td>
                      {data.single && <td className="px-3 py-2 text-right font-bold">{Number(r.saldo_producto).toLocaleString("es-EC", { maximumFractionDigits: 3 })}</td>}
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.odoo_task_id ? (
                          <Link to={`/ot-smart/${r.odoo_task_id}`} className="text-blue-700 font-bold hover:underline">{r.ot || `#${r.odoo_task_id}`}</Link>
                        ) : <span className="text-gray-300">—</span>}
                        {r.enlace_ot && <div className="text-[9px] text-gray-400 flex items-center gap-0.5"><Link2 size={9} />{r.enlace_ot}</div>}
                      </td>
                      <td className="px-3 py-2"><div>{r.cliente || ""}</div><div className="text-[10px] text-gray-400">{r.tecnico || ""}</div></td>
                      <td className="px-3 py-2 text-[10px] text-gray-500">{r.desde_ubicacion} → {r.hacia_ubicacion}</td>
                    </tr>
                  ))}
                  {!data.rows?.length && (
                    <tr><td colSpan={data.single ? 11 : 10} className="px-3 py-8 text-center text-gray-400">Sin movimientos en el rango.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3">
      <div className="text-[10px] font-bold uppercase text-gray-500">{label}</div>
      <div className="text-xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
